import axios from "axios";
import { URL } from "../Urls";
import Cookies from "universal-cookie/es6";

const savedQueryListRequestUrl = (sport_type) =>
  `${URL}get_saved_query/${encodeURIComponent(sport_type)}/`;

const cookies = new Cookies();

/** Universal-cookie can miss the token in some environments; also read document.cookie. */
function getJwtForApi() {
    const fromLib = cookies.get("jwt");
    if (fromLib) {
        return fromLib;
    }
    if (typeof document === "undefined") {
        return undefined;
    }
    const m = document.cookie.match(/(?:^|; )jwt=([^;]*)/);
    if (!m) {
        return undefined;
    }
    return decodeURIComponent(m[1].replace(/\+/g, " "));
}

function authHeaders() {
    const t = getJwtForApi();
    return t ? { Authorization: t } : {};
}

/** get_saved_query returns a JSON array; some proxies or versions may wrap the list. */
function normalizeListPayload(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (payload && Array.isArray(payload.data)) {
        return payload.data;
    }
    if (payload && Array.isArray(payload.results)) {
        return payload.results;
    }
    if (payload && Array.isArray(payload.items)) {
        return payload.items;
    }
    return null;
}

/**
 * Fetches saved queries for a sport. Always returns { ok, data, error? }.
 * - ok + data: success (data is always an array)
 * - ok: false + error: { code, message, requestUrl, status? }
 */
export const getSavedQueries = async (sport_type) => {
  const requestUrl = savedQueryListRequestUrl(sport_type);
  const config = { headers: authHeaders() };
  const fail = (code, message, extra = {}) => ({ ok: false, data: [], error: { code, message, requestUrl, ...extra } });
  const ok = (data) => ({ ok: true, data: Array.isArray(data) ? data : [] });

  try {
    const res = await axios.get(requestUrl, config);
    if (res.status !== 200) {
      return fail("http", `get_saved_query returned status ${res.status} (expected 200).`, {
        status: res.status,
      });
    }
    let body = res.data;
    if (body == null) {
      return ok([]);
    }
    if (typeof body === "string") {
      const t = body.trim();
      if (!t) {
        return ok([]);
      }
      try {
        body = JSON.parse(t);
      } catch (e) {
        console.error("getSavedQueries: response was not valid JSON", t.substring(0, 400));
        return fail(
          "bad_format",
          "The server returned 200 but the body was not valid JSON (expected a JSON array)."
        );
      }
    }
    const list = normalizeListPayload(body);
    if (list !== null) {
      return ok(list);
    }
    console.error("getSavedQueries: response was not an array", body);
    return fail("bad_format", "The server returned 200 but the body was not a JSON array. Check the Network tab for this response shape.");
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return ok([]);
    }
    if (error.response && error.response.status === 401) {
      return fail(
        "unauthorized",
        "Not signed in, or the token is not accepted. Sign in again. " +
          "If you use a local app (e.g. localhost:3000) with REACT_APP_BACKEND_URL pointing at the live API, the browser will not send the same cookie — use a local backend URL in .env (e.g. http://127.0.0.1:8000/) and restart `npm start`."
      );
    }
    const st = error.response && error.response.status;
    if (st >= 500) {
      const d = error.response && error.response.data;
      let detail = "";
      if (d) {
        if (typeof d === "string") {
          detail = d;
        } else if (d.detail) {
          detail = typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail);
        } else if (d.error) {
          detail = [d.error, d.detail].filter(Boolean).join(" ");
        } else {
          try {
            detail = JSON.stringify(d);
          } catch (e) {
            detail = "";
          }
        }
      }
      const base = detail
        ? `Server error (${st}): ${detail}`
        : `Server error (${st}). `;
      const tail =
        " Check the terminal where Django is running for the full traceback. If the message above mentions a missing column (e.g. market), run: python manage.py migrate in your backend project.";
      return fail("server", `${base}${tail}`, { status: st, serverBody: d });
    }
    if (st === 403) {
      return fail("forbidden", `Request forbidden (${st}).`, { status: st });
    }
    if (error.response) {
      return fail("http", `Request failed with status ${st}.`, { status: st });
    }
    if (!error.response && error.request) {
      return fail(
        "network",
        `Could not reach the API at ${requestUrl}. ` +
          `In development the app now defaults the API to http://127.0.0.1:8000/ (see src/Urls.js). ` +
          `Start the Django dev server (e.g. runserver) on that port, or set REACT_APP_BACKEND_URL in .env to your backend URL and restart "npm start".`
      );
    }
    console.error("getSavedQueries failed", error);
  }
  return fail(
    "unknown",
    "Unknown error loading saved queries. Check the browser console and Network tab for get_saved_query."
  );
};

export const getSavedQuery = async (queryId) => {
    const config = { headers: authHeaders() }
    try {
        const res = await axios.get(URL + `get_one_saved_query/${queryId}/`, config);
        if (res.status === 200) {
            return res.data;
        }
    } catch (error) {
        console.error(error)
    }
    return false;
};

export const postSavedQuery = async (data) => {
    const config = { headers: authHeaders() }
    return await axios.post(URL + "save_query/", data, config);
    // try {
    //     if (res.status === 201) {
    //         return res.data
    //     }
    // } catch (error) {
    //     console.error(error)
    // }
    // return false
};

export const deleteSavedQuery = async (data) => {
    const config = { headers: authHeaders() }
    const res = await axios.post(URL + "delete_saved_query/", data, config);
    if (res.status === 200) {
        return res.data
    }
    return false
};

export const updateSavedQuery = async (data, queryId) => {
    const config = { headers: authHeaders() }
    try {
        const res = await axios.patch(URL + `update_saved_query/${queryId}/`, data, config);
        if (res.status === 200) {
            return res.data
        }
    } catch (error) {
        console.error(error)
    }
    return false
};

export const processSavedQuery = async (data) => {
    const config = { headers: authHeaders() }
    try {
        const res = await axios.post(URL + `process_saved_queries/`, data, config);
        if (res.status === 200) {
            return res.data
        }
    } catch (error) {
        console.error(error)
    }
    return false
};

export const deactivateSavedQuery = async (data) => {
    const config = { headers: authHeaders() }
    try {
        const res = await axios.post(URL + `deactivate_saved_queries/`, data, config);
        if (res.status === 200) {
            return res.data
        }
    } catch (error) {
        console.error(error)
    }
    return false
};


// axios({
//     method: "post",
//     //url: "https://sdql-testapp.herokuapp.com/isAuthenticated/",
//     url: URL + "isAuthenticated/",
//     data: {
//       jwtCookie: props.jwtCookie,
//     },
//   }).then((res) => {
//     //console.log("APP: ", res)
//     if (res.data.message === "OK") {
//       props.doLogin();
//     }
// });
